import { type MetaFunction, type DataFunctionArgs, json } from '@remix-run/node'
import { Outlet, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { ProfessionalList } from '~/features/home/professional-list.tsx'
import { MainContainer } from '~/components/layout/main-container.tsx'
import { MainLayout } from '~/components/layout/main-layout.tsx'
import { HomeProfessionalSessionCard } from '~/components/cards/home-professional-session-card.tsx'
import { getServicesSlugData } from '~/features/home/getServicesSlugData.server.ts'
import { Spacer } from '~/components/ui/spacer.tsx'
import { InfiniteScroll } from '~/features/home/infiniteScroll.tsx'
import { getUserId, getUserRole } from '~/features/auth/auth.server.ts'
import { prisma } from '~/utils/config/db.server.ts'
import { EnvEnum, type ModeEnvType } from 'types/enums.ts'

export const meta: MetaFunction = () => [
  { title: 'Miistico' },
  {
    tagName: 'link',
    rel: 'canonical',
    href:
      ENV.MODE === (EnvEnum.PRODUCTION as ModeEnvType)
        ? 'https://www.miistico.com/'
        : 'https://miistico-staging.fly.dev/',
  },
  ...(ENV.MODE === (EnvEnum.PRODUCTION as ModeEnvType)
    ? [{ name: 'robots', content: 'index, follow' }]
    : [{ name: 'robots', content: 'noindex, nofollow' }]),
]

export async function loader({ request }: DataFunctionArgs) {
  // OPTIMIZACIÓN: Obtener userId y userRole primero para evitar consultas duplicadas
  const userId = await getUserId(request)
  const userRole = await getUserRole(request)

  // OPTIMIZACIÓN: Pasar userId y userRole para evitar consultas duplicadas en getServicesSlugData
  const servicesData = await getServicesSlugData(request, userId, userRole)

  if (!userId) {
    return json({
      ...servicesData,
      unreadMessagesCount: null,
    })
  }

  const unreadMessagesCount = await prisma.message.count({
    where: {
      isShown: false,
      userId: {
        not: userId,
      },
      conversation: {
        session: {
          clientId: userId,
        },
      },
    },
  })

  return json({
    ...servicesData,
    unreadMessagesCount,
  })
}

export default function DefaultRoute() {
  const data = useLoaderData<typeof loader>()

  return (
    <MainLayout
      userRole={data?.userRole as any}
      unreadMessagesCount={data?.unreadMessagesCount ?? undefined}
    >
      <MainContainer>
        <ProfessionalList
          variant="services"
          categoriesSelectData={data.categoriesSelectData}
          topicsCarouselData={data.topicsCarouselData as any} //This topic[] includes the topicIcon so needs the any
          topicsSelectData={data.topicsSelectData}
          resultsNumber={data.services.length}
          totalItems={data.totalServices}
        >
          <InfiniteScroll
            apiEndpoint="/api/services-paginated"
            className="xl:grid-cols-2"
            initialItems={data.services}
            shuffleSeed={data.shuffleSeed}
            renderItem={(service: any) => (
              <HomeProfessionalSessionCard
                key={service.id}
                service={service}
                currency={data.currency}
              />
            )}
          />
          <Spacer size="2xs" />
        </ProfessionalList>
      </MainContainer>
      <Outlet />
    </MainLayout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
